import React, { FC } from 'react';
import { Tab, useTranslate, usePermissions, useShowController, ReferenceField } from 'react-admin';
import Avatar from '@material-ui/core/Avatar';
import SvgIcon from '@material-ui/core/SvgIcon';
import { Grid, makeStyles, Theme } from '@material-ui/core';
import TagsIcon from '@material-ui/icons/LocalOffer';
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet';
import { RibbonIcon } from '~/img';
import StarIcon from '@material-ui/icons/Star';
import LocationOnIcon from '@material-ui/icons/LocationOn';

import { Show, PaperTabbedShowLayout, AsideBase } from '~/layout';
import { l } from './PnlAccountList';
import { ArchiveIcon as PnLIcon } from '~/img';
import Details from './components/Details';
import { NoPermissions, TextField as TextFieldRa } from '~/components/ra';
import BankDaySummary from './components/BankDaySummary';
import { TextField, StatusField, GlTypeIcon, TransactionsHistory } from '~/components';
import { useRightContentSpacing } from '~/hooks/styles';
import { PnLAccount } from '~/types/account';
import { useHistory } from 'react-router-dom';
import { useEditMode } from '~/hooks/useEditModeContext';
import { useChangelogRecord } from '~/hooks/useChangelogContext';

const PnlAccountShow: FC<any> = (props) => {
  const { permissions } = usePermissions();
  const translate = useTranslate();
  const t = (key: string): string => translate(l(key));

  const history = useHistory();
  const showController = useShowController(props);
  const { editMode } = useEditMode();
  const showRecord: PnLAccount = useChangelogRecord(showController.record);
  const showResource = showController.resource;

  const spacing = useRightContentSpacing();
  const classes = useStyles({ spacing });

  const currentPathname = history?.location.pathname;
  const transactionsPathname = `/${showResource}/${showRecord?.id || ''}/show/transactions`;

  if (!permissions) return null;
  if (!permissions?.includes('tenant.account.view')) return <NoPermissions />;

  return (
    <Show
      icon={
        <Avatar>
          <SvgIcon>
            <PnLIcon />
          </SvgIcon>
        </Avatar>
      }
      {...props}
    >
      <PaperTabbedShowLayout>
        <Tab label={t('tabs.details')}>
          <Details permissions={permissions} />
        </Tab>
        {permissions?.includes('tenant.account.daySummary.list') && (
          <Tab
            label={t('tabs.bankDaySummary')}
            path='bankDaySummary'
            disabled={editMode !== 'VIEW'}
          >
            <BankDaySummary permissions={permissions} />
          </Tab>
        )}
        {permissions?.includes('tenant.account.transaction.list') && (
          <Tab label={t('tabs.transactions')} path='transactions' disabled={editMode !== 'VIEW'}>
            <TransactionsHistory
              permissions={permissions}
              record={showRecord}
              resource={'tenant/accounts'}
            />
          </Tab>
        )}
      </PaperTabbedShowLayout>
      {currentPathname !== transactionsPathname && showRecord && (
        <Grid container item xs direction='column' spacing={spacing}>
          <Grid item className={classes.rightContent}>
            <AsideBase>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField label={t('accountNumber')} icon={<AccountBalanceWalletIcon />}>
                    {showRecord.number}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField label={t('status')} icon={<RibbonIcon />}>
                    <StatusField status={showRecord.status} />
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <ReferenceField
                    source='productId'
                    record={showRecord}
                    reference='products'
                    link={false}
                    addLabel={false}
                  >
                    <TextFieldRa source='name' label={t('productId')} icon={<StarIcon />} />
                  </ReferenceField>
                </Grid>
                {showRecord.type && (
                  <Grid item xs={12}>
                    <TextField label={t('type')} icon={<TagsIcon />}>
                      {showRecord.type}
                    </TextField>
                  </Grid>
                )}
                {showRecord.glType && (
                  <Grid item xs={12}>
                    <TextField label={t('glType')} icon={<TagsIcon />}>
                      <GlTypeIcon code={showRecord.glType} variant='text' />
                    </TextField>
                  </Grid>
                )}
                {showRecord.branch && (
                  <Grid item xs={12}>
                    <TextField label={t('branch')} icon={<LocationOnIcon />}>
                      {showRecord.branch}
                    </TextField>
                  </Grid>
                )}
              </Grid>
            </AsideBase>
          </Grid>
        </Grid>
      )}
    </Show>
  );
};

type StylesProps = {
  spacing: number;
};

const useStyles = makeStyles<Theme, StylesProps>((theme) => ({
  rightContent: {
    marginLeft: ({ spacing }) => theme.spacing(spacing),
  },
  marginLeftAside: {
    marginLeft: '24px',
  },
  bottomSpacing: {
    paddingBottom: theme.spacing(2),
  },
  headerSection: {
    '& .sectionActions': {
      opacity: 0.5,
      transition: `opacity ${theme.transitions.easing.easeIn}`,
      transitionDuration: `${theme.transitions.duration.shortest}ms`,
    },
    '&:hover': {
      '& .sectionActions': {
        opacity: 1,
      },
    },
  },
  error: {
    color: theme.palette.error.main,
  },
}));

export default PnlAccountShow;
